import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PortalLayout from './PortalLayout';
import { inventoryService, translationService, expenseService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BulkAddModal from '../../components/BulkAddModal';
import RecipeImportModal from '../../components/RecipeImportModal';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import MobileBanner from '../../components/common/MobileBanner';
import { C } from '../../styles/theme';

// ─── Brand tokens ────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Shared primitives ────────────────────────────────────────────────────────

const Badge = ({ color, bg, children }) => (
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, borderRadius: 5, padding: '2px 8px', background: bg, color, display: 'inline-block' }}>
        {children}
    </span>
);

const StockBadge = ({ item, t }) => {
    const stock = parseFloat(item.current_stock) || 0;
    const min = parseFloat(item.minimum_stock_level) || 0;
    if (stock <= 0) return <Badge color={C.red} bg={C.redLight}>{t('out')}</Badge>;
    if (min > 0 && stock <= min) return <Badge color={C.amber} bg={C.amberLight}>{t('low')}</Badge>;
    return <Badge color={C.green} bg={C.greenLight}>{t('good')}</Badge>;
};

// ─── Toast ────────────────────────────────────────────────────────────────────

const ToastContainer = ({ toasts }) => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: t.type === 'success' ? '#eff5ff' : C.redLight,
                border: `1.5px solid ${t.type === 'success' ? '#86efac' : '#fca5a5'}`,
                color: t.type === 'success' ? '#16a34a' : C.red,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontFamily: FONT,
                pointerEvents: 'auto',
                minWidth: 220,
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

let _toastId = 0;
const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const add = useCallback((message, type = 'success') => {
        const id = ++_toastId;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
    }, []);
    return { toasts, add };
};

// ─── Modal wrapper ────────────────────────────────────────────────────────────

const Modal = ({ open, onClose, title, width = 520, children }) => {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
            <div className="portal-modal-card" style={{ background: C.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: FONT }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: C.text }}>{title}</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, lineHeight: 1, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Close">
                        ×
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// ─── Form primitives ──────────────────────────────────────────────────────────

const Label = ({ children, required }) => (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>
        {children}{required && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
    </label>
);

const inputStyle = {
    width: '100%',
    border: `1.5px solid ${C.border}`,
    borderRadius: 7,
    padding: '9px 12px',
    fontSize: 13,
    color: C.text,
    fontFamily: FONT,
    outline: 'none',
    background: C.white,
    boxSizing: 'border-box',
};

const Input = (props) => (
    <input
        {...props}
        style={{ ...inputStyle, ...(props.style || {}) }}
        onFocus={e => { e.target.style.borderColor = C.green; }}
        onBlur={e => { e.target.style.borderColor = C.border; }}
    />
);

const Select = ({ children, ...props }) => (
    <select
        {...props}
        style={{ ...inputStyle, ...(props.style || {}), appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 30 }}
        onFocus={e => { e.target.style.borderColor = C.green; }}
        onBlur={e => { e.target.style.borderColor = C.border; }}
    >
        {children}
    </select>
);

const Textarea = (props) => (
    <textarea
        {...props}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 72, ...(props.style || {}) }}
        onFocus={e => { e.target.style.borderColor = C.green; }}
        onBlur={e => { e.target.style.borderColor = C.border; }}
    />
);

const BtnPrimary = ({ children, style, ...props }) => (
    <button {...props} style={{ background: C.green, color: C.white, border: 'none', borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer', opacity: props.disabled ? 0.55 : 1, fontFamily: FONT, ...style }}>
        {children}
    </button>
);

const BtnSecondary = ({ children, style, ...props }) => (
    <button {...props} style={{ background: C.white, color: C.muted, border: `1.5px solid ${C.border}`, borderRadius: 7, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, ...style }}>
        {children}
    </button>
);

const BtnDanger = ({ children, style, ...props }) => (
    <button {...props} style={{ background: C.red, color: C.white, border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, ...style }}>
        {children}
    </button>
);

// ─── Table primitives ─────────────────────────────────────────────────────────

const Table = ({ children }) => (
    <div className="portal-table-wrap" style={{ border: `1.5px solid ${C.border}`, borderRadius: 12, overflowX: 'auto', overflowY: 'hidden', background: C.white }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>{children}</table>
    </div>
);

const TH = ({ children, style }) => (
    <th style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, background: C.bg, borderBottom: `1.5px solid ${C.border}`, padding: '10px 14px', textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2, ...style }}>
        {children}
    </th>
);

const TD = ({ children, style }) => (
    <td style={{ fontSize: 13, color: C.text, borderBottom: `1px solid #f1f5f9`, padding: '12px 14px', ...style }}>
        {children}
    </td>
);

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = ({ icon, title, sub, cta, onCta }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '56px 24px', fontFamily: FONT }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            {icon}
        </div>
        <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: C.text }}>{title}</p>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: C.muted, textAlign: 'center', maxWidth: 280 }}>{sub}</p>
        {cta && <BtnPrimary onClick={onCta}>{cta}</BtnPrimary>}
    </div>
);

// ─── SVG icons ────────────────────────────────────────────────────────────────

const IconBox = ({ size = 22, color = C.green }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

const IconRecipe = ({ size = 22, color = C.green }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
    </svg>
);

const IconPlus = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const IconSearch = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const IconEdit = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);

const IconTrash = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

const IconArrow = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
);

// ─── Unit options ─────────────────────────────────────────────────────────────

const UNIT_OPTIONS = ['grams', 'ml', 'pieces', 'kg', 'liters', 'cups', 'tablespoons', 'teaspoons'];

// ─── Unit conversion helpers ─────────────────────────────────────────────────

const UNIT_CONVERSIONS = {
    'litres': { small: 'ml', factor: 1000 },
    'liters': { small: 'ml', factor: 1000 },
    'kg': { small: 'grams', factor: 1000 },
};

const getRecipeUnitOptions = (stockUnit) => {
    const conv = UNIT_CONVERSIONS[stockUnit];
    if (conv) return [stockUnit, conv.small];
    return [stockUnit];
};

const convertToStockUnit = (value, recipeUnit, stockUnit) => {
    if (recipeUnit === stockUnit) return value;
    const conv = UNIT_CONVERSIONS[stockUnit];
    if (conv && recipeUnit === conv.small) return value / conv.factor;
    return value;
};

const convertFromStockUnit = (value, recipeUnit, stockUnit) => {
    if (recipeUnit === stockUnit) return value;
    const conv = UNIT_CONVERSIONS[stockUnit];
    if (conv && recipeUnit === conv.small) return value * conv.factor;
    return value;
};

const hasConversion = (stockUnit) => !!UNIT_CONVERSIONS[stockUnit];

// ─── Ingredient form default ──────────────────────────────────────────────────

const EMPTY_ITEM = { name: '', unit_of_measurement: '', current_stock: 0, cost_per_unit: '', _total_cost: '', minimum_stock_level: '', supplier: '', recipe_unit: null, purchase_unit: '', purchase_unit_size: '', translation_mode: null, manual_translation: null };
const EMPTY_TX = { transaction_type: 'purchase', quantity: '', cost_per_unit: '', total_cost: '', notes: '', use_purchase_unit: false, purchase_quantity: '' };

// ─── Cost History Drawer ──────────────────────────────────────────────────────

const CostHistoryDrawer = ({ item, open, onClose, t, currency }) => {
    const [transactions, setTransactions] = useState([]);
    const [dateRange, setDateRange] = useState('6m');
    const [loading, setLoading] = useState(false);
    const [txFilter, setTxFilter] = useState('purchases');

    useEffect(() => {
        if (!open || !item) return;
        let active = true;
        setLoading(true);
        const now = new Date();
        let dateFrom = null;
        if (dateRange === '30d') dateFrom = new Date(now.getTime() - 30 * 86400000);
        else if (dateRange === '3m') dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        else if (dateRange === '6m') dateFrom = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        const fromStr = dateFrom ? dateFrom.toISOString().split('T')[0] : null;
        inventoryService.getInventoryTransactions(item.inventory_id, fromStr)
            .then(data => { if (active) setTransactions(data || []); })
            .catch(() => { if (active) setTransactions([]); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [open, item, dateRange]);

    if (!open || !item) return null;

    const purchases = transactions
        .filter(tx => tx.transaction_type === 'purchase')
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const chartData = purchases.map(tx => ({
        date: new Date(tx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        cost: parseFloat(tx.cost_per_unit) || 0,
    }));

    const firstCost = chartData.length > 1 ? chartData[0].cost : 0;
    const lastCost = chartData.length > 1 ? chartData[chartData.length - 1].cost : 0;
    const pctChange = firstCost > 0 ? ((lastCost - firstCost) / firstCost * 100).toFixed(1) : 0;
    const trendColor = pctChange > 0 ? C.red : pctChange < 0 ? C.green : C.muted;

    const typeBadge = (type) => ({
        purchase: { bg: C.greenLight, color: C.green },
        adjustment: { bg: '#fffbeb', color: '#d97706' },
        waste: { bg: '#fef2f2', color: C.red },
    }[type] || { bg: C.bg, color: C.muted });

    return (
        <>
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.2)', zIndex: 999 }} onClick={onClose} />
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, backgroundColor: C.white,
                boxShadow: '-4px 0 16px rgba(0,0,0,0.08)', zIndex: 1000, display: 'flex',
                flexDirection: 'column', fontFamily: FONT,
            }}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>{item.name}</h3>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
                                {t('current_cost')}: {currency}{(parseFloat(item.cost_per_unit) || 0).toFixed(2)} / {item.unit_of_measurement}
                            </p>
                        </div>
                        <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>✕</button>
                    </div>
                </div>

                {chartData.length > 1 && (
                    <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ height: 120 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.muted }} />
                                    <YAxis tick={{ fontSize: 10, fill: C.muted }} width={40} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="cost" stroke={trendColor} strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <p style={{ fontSize: 12, color: trendColor, fontWeight: 600, marginTop: 8, marginBottom: 0 }}>
                            {pctChange > 0 ? '+' : ''}{pctChange}% {t('vs_period_start')}
                        </p>
                    </div>
                )}

                <div style={{ padding: '12px 24px', display: 'flex', gap: 8 }}>
                    {[
                        { value: '30d', label: t('last_30_days') },
                        { value: '3m', label: t('last_3_months') },
                        { value: '6m', label: t('last_6_months') },
                        { value: 'all', label: t('all_time') },
                    ].map(opt => (
                        <button key={opt.value} onClick={() => setDateRange(opt.value)} style={{
                            padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                            border: `1px solid ${dateRange === opt.value ? C.green : C.border}`,
                            backgroundColor: dateRange === opt.value ? C.greenLight : C.white,
                            color: dateRange === opt.value ? C.green : C.muted, cursor: 'pointer',
                        }}>{opt.label}</button>
                    ))}
                </div>

                <div style={{ padding: '0 24px 8px', display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}` }}>
                    {[
                        { value: 'purchases', label: t('purchases_only') },
                        { value: 'all', label: t('all_transactions') },
                    ].map(opt => (
                        <button key={opt.value} onClick={() => setTxFilter(opt.value)} style={{
                            padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            border: 'none', backgroundColor: 'transparent',
                            borderBottom: txFilter === opt.value ? `2px solid ${C.green}` : '2px solid transparent',
                            color: txFilter === opt.value ? C.green : C.muted,
                        }}>{opt.label}</button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
                    {(() => {
                        const displayed = txFilter === 'purchases'
                            ? transactions.filter(tx => tx.transaction_type === 'purchase')
                            : transactions;
                        if (loading) return <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', marginTop: 24 }}>{t('loading')}</p>;
                        if (displayed.length === 0) return <p style={{ color: C.muted, fontSize: 13, textAlign: 'center', marginTop: 24 }}>{txFilter === 'purchases' ? t('no_purchases_recorded') : t('no_transactions')}</p>;
                        return (
                        <div className="portal-table-wrap">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: 8, textAlign: 'left', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t('date')}</th>
                                    <th style={{ padding: 8, textAlign: 'left', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t('type')}</th>
                                    <th style={{ padding: 8, textAlign: 'right', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t('quantity')}</th>
                                    <th style={{ padding: 8, textAlign: 'right', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t('unit_cost')}</th>
                                    <th style={{ padding: 8, textAlign: 'right', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{t('total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayed.map((tx, i) => {
                                    const badge = typeBadge(tx.transaction_type);
                                    return (
                                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                            <td style={{ padding: 8, color: C.text }}>
                                                {new Date(tx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                            </td>
                                            <td style={{ padding: 8 }}>
                                                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: badge.bg, color: badge.color }}>
                                                    {tx.transaction_type}
                                                </span>
                                            </td>
                                            <td style={{ padding: 8, textAlign: 'right', color: C.text }}>
                                                {tx.quantity} {item.unit_of_measurement}
                                            </td>
                                            <td style={{ padding: 8, textAlign: 'right', color: C.text }}>
                                                {tx.cost_per_unit ? `${currency}${parseFloat(tx.cost_per_unit).toFixed(2)}` : '—'}
                                            </td>
                                            <td style={{ padding: 8, textAlign: 'right', color: C.text }}>
                                                {tx.total_cost ? `${currency}${parseFloat(tx.total_cost).toFixed(2)}` : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                        );
                    })()}
                </div>
            </div>
        </>
    );
};

// ─── Tab 1: Ingredients ───────────────────────────────────────────────────────

const IngredientsTab = ({ toast }) => {
    const { getCurrency } = useAuth();
    const { t, translationDetails } = useLanguage();
    const currency = getCurrency();
    const isMobile = useIsMobile();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [hoveredRow, setHoveredRow] = useState(null);

    // Modals
    const [addEditOpen, setAddEditOpen] = useState(false);
    const [txOpen, setTxOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [correctionOpen, setCorrectionOpen] = useState(false);
    const [showBulkAdd, setShowBulkAdd] = useState(false);
    const [showRecipeImport, setShowRecipeImport] = useState(false);
    const [historyItem, setHistoryItem] = useState(null);

    const [editingItem, setEditingItem] = useState(null);
    const [txItem, setTxItem] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [correctionForm, setCorrectionForm] = useState({ cost_per_unit: '', effective_from: '', reason: '' });

    const [form, setForm] = useState(EMPTY_ITEM);
    const [tx, setTx] = useState(EMPTY_TX);
    const [saving, setSaving] = useState(false);

    // Translation state
    const [translating, setTranslating] = useState(false);
    const [translationOptions, setTranslationOptions] = useState(null);
    const [showTranslationPicker, setShowTranslationPicker] = useState(false);

    const loadItems = useCallback(async () => {
        try {
            setLoading(true);
            const data = await inventoryService.getAllInventory();
            setItems(data);
        } catch {
            toast(t('failed_to_load_inventory'), 'error');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { loadItems(); }, [loadItems]);

    const openAdd = () => { setEditingItem(null); setForm(EMPTY_ITEM); setTranslationOptions(null); setShowTranslationPicker(false); setAddEditOpen(true); };
    const openEdit = (item) => {
        setEditingItem(item);
        const stock = parseFloat(item.current_stock) || 0;
        const cpu = parseFloat(item.cost_per_unit) || 0;
        setForm({ name: item.name, unit_of_measurement: item.unit_of_measurement, current_stock: item.current_stock, cost_per_unit: item.cost_per_unit, _total_cost: stock > 0 && cpu > 0 ? (stock * cpu).toFixed(2) : '', minimum_stock_level: item.minimum_stock_level ?? '', supplier: item.supplier ?? '', recipe_unit: item.recipe_unit || null, purchase_unit: item.purchase_unit || '', purchase_unit_size: item.purchase_unit_size ?? '', translation_mode: item.translation_mode || null, manual_translation: item.manual_translation || null });
        // Pre-populate translation options if mode already set
        if (item.translation_mode && translationDetails?.[item.name]) {
            const ar = translationDetails[item.name]?.ar || {};
            setTranslationOptions({
                translation: ar.translation || null,
                transliteration: ar.transliteration || null,
                community_manuals: ar.community_manuals || [],
                suggestion: null,
            });
        } else {
            setTranslationOptions(null);
        }
        setShowTranslationPicker(false);
        setAddEditOpen(true);
    };
    const openTx = (item) => { setTxItem(item); setTx({ ...EMPTY_TX, cost_per_unit: item.cost_per_unit, use_purchase_unit: !!item.purchase_unit, purchase_quantity: '' }); setTxOpen(true); };
    const openDelete = (item) => { setDeleteTarget(item); setDeleteOpen(true); };

    const handleFormChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleTranslate = async () => {
        if (!form.name) return;
        setTranslating(true);
        try {
            const result = await translationService.translateProductName(form.name, 'ingredient');
            const aiFailed = result.translation === form.name && result.transliteration === form.name;
            if (!aiFailed) {
                setTranslationOptions(result);
                setShowTranslationPicker(true);
                if (!form.translation_mode) {
                    setForm(f => ({ ...f, translation_mode: 'translation' }));
                }
            }
        } catch (err) {
            console.error('Translation failed:', err);
        }
        finally { setTranslating(false); }
    };

    const handleTxChange = (e) => {
        const { name, value } = e.target;
        setTx(prev => {
            const next = { ...prev, [name]: value };
            // Auto-convert purchase_quantity to stock quantity
            if (name === 'purchase_quantity' && next.use_purchase_unit && txItem?.purchase_unit_size) {
                const pq = parseFloat(value) || 0;
                next.quantity = (pq * txItem.purchase_unit_size).toFixed(2);
            }
            if ((name === 'quantity' || name === 'cost_per_unit') && next.transaction_type === 'purchase') {
                const q = parseFloat(name === 'quantity' ? value : next.quantity) || 0;
                const c = parseFloat(name === 'cost_per_unit' ? value : next.cost_per_unit) || 0;
                next.total_cost = (q * c).toFixed(2);
            }
            if (name === 'purchase_quantity' && next.transaction_type === 'purchase') {
                const q = parseFloat(next.quantity) || 0;
                const c = parseFloat(next.cost_per_unit) || 0;
                next.total_cost = (q * c).toFixed(2);
            }
            return next;
        });
    };

    const handleSaveItem = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.unit_of_measurement) {
            toast(t('please_fill_required_fields'), 'error'); return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                cost_per_unit: parseFloat(form.cost_per_unit),
                current_stock: parseFloat(form.current_stock) || 0,
                minimum_stock_level: form.minimum_stock_level !== '' ? parseFloat(form.minimum_stock_level) : null,
                recipe_unit: hasConversion(form.unit_of_measurement) ? (form.recipe_unit || form.unit_of_measurement) : null,
                purchase_unit: form.purchase_unit?.trim() || null,
                purchase_unit_size: form.purchase_unit?.trim() && form.purchase_unit_size !== '' ? parseFloat(form.purchase_unit_size) : null,
                translation_mode: form.translation_mode || null,
                manual_translation: form.manual_translation || null,
            };
            if (editingItem) {
                await inventoryService.updateInventoryItem(editingItem.inventory_id, payload);
                toast(t('ingredient_updated_successfully'));
            } else {
                const created = await inventoryService.createInventoryItem(payload);
                if (payload.current_stock > 0 && payload.cost_per_unit > 0) {
                    try {
                        const bdState = await tenantStorage.getBusinessDayState();
                        const hasActiveDay = bdState.isBusinessDay && bdState.salesDate;
                        await expenseService.createExpense({
                            authorizer: 'system',
                            category: 'Inventory',
                            product: payload.name,
                            price_per_unit: payload.cost_per_unit,
                            quantity: Math.round(payload.current_stock),
                            total_cost: payload.current_stock * payload.cost_per_unit,
                            expense_type: hasActiveDay ? 'daily' : 'overhead',
                            sales_date: hasActiveDay ? bdState.salesDate : null,
                            date: new Date().toISOString().split('T')[0],
                            time: new Date().toTimeString().split(' ')[0],
                        });
                    } catch (e) { /* expense creation failed silently */ }
                }
                toast(t('ingredient_added_successfully'));
            }
            setAddEditOpen(false);
            await loadItems();
        } catch {
            toast(t('failed_to_save_ingredient'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await inventoryService.deleteInventoryItem(deleteTarget.inventory_id);
            toast(t('ingredient_deleted'));
            setDeleteOpen(false);
            await loadItems();
        } catch (err) {
            toast(err?.response?.data?.detail || t('failed_to_delete_ingredient'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleTxSubmit = async (e) => {
        e.preventDefault();
        if (!tx.quantity) { toast(t('please_enter_quantity'), 'error'); return; }
        setSaving(true);
        try {
            let qty = parseFloat(tx.quantity);
            if (tx.transaction_type === 'waste') qty = -Math.abs(qty);
            const costPerUnit = tx.cost_per_unit ? parseFloat(tx.cost_per_unit) : null;
            const totalCost = tx.total_cost ? parseFloat(tx.total_cost) : null;
            await inventoryService.createInventoryTransaction({
                inventory_id: txItem.inventory_id,
                transaction_type: tx.transaction_type,
                quantity: qty,
                cost_per_unit: costPerUnit,
                total_cost: totalCost,
                notes: tx.notes,
            });
            if (tx.transaction_type === 'purchase' && totalCost) {
                try {
                    const bdState = await tenantStorage.getBusinessDayState();
                    const hasActiveDay = bdState.isBusinessDay && bdState.salesDate;
                    await expenseService.createExpense({
                        authorizer: 'system',
                        category: 'Inventory',
                        product: txItem.name,
                        price_per_unit: costPerUnit || 0,
                        quantity: Math.round(Math.abs(qty)),
                        total_cost: totalCost,
                        expense_type: hasActiveDay ? 'daily' : 'overhead',
                        sales_date: hasActiveDay ? bdState.salesDate : null,
                        date: new Date().toISOString().split('T')[0],
                        time: new Date().toTimeString().split(' ')[0],
                        inventory_id: txItem.inventory_id,
                    });
                } catch (e) { /* expense creation failed, transaction still recorded */ }
            }
            toast(t('transaction_recorded'));
            setTxOpen(false);
            await loadItems();
        } catch (err) {
            toast(err?.response?.data?.detail || t('failed_to_record_transaction'), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Filter
    const filtered = items.filter(item => {
        const matchSearch = !search || item.name.toLowerCase().includes(search.toLowerCase()) || (item.supplier || '').toLowerCase().includes(search.toLowerCase());
        if (!matchSearch) return false;
        if (statusFilter === 'all') return true;
        const stock = parseFloat(item.current_stock) || 0;
        const min = parseFloat(item.minimum_stock_level) || 0;
        if (statusFilter === 'good') return stock > 0 && (min === 0 || stock > min);
        if (statusFilter === 'low') return stock > 0 && min > 0 && stock <= min;
        if (statusFilter === 'out') return stock <= 0;
        return true;
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <IconSearch />
                    </span>
                    <input
                        placeholder={t('search_ingredients_or_supplier')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: 34 }}
                        onFocus={e => { e.target.style.borderColor = C.green; }}
                        onBlur={e => { e.target.style.borderColor = C.border; }}
                    />
                </div>
                <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 140 }}>
                    <option value="all">{t('all_status')}</option>
                    <option value="good">{t('good')}</option>
                    <option value="low">{t('low_stock')}</option>
                    <option value="out">{t('out_of_stock')}</option>
                </Select>
                <button onClick={() => setShowBulkAdd(true)} style={{
                    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    border: `1px solid ${C.green}`, backgroundColor: C.white, color: C.green, cursor: 'pointer',
                }}>{t('bulk_add')}</button>
                <button onClick={() => setShowRecipeImport(true)} style={{
                    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    border: `1px solid ${C.green}`, backgroundColor: C.white, color: C.green, cursor: 'pointer',
                }}>{t('import_recipes')}</button>
                <BtnPrimary onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconPlus color="white" /> {t('add_ingredient')}
                </BtnPrimary>
            </div>

            {/* Stats bar — desktop only */}
            {!isMobile && !loading && filtered.length > 0 && (
                <div style={{ display: 'flex', gap: 20, fontSize: 12, color: C.muted }}>
                    <span>{filtered.length} {filtered.length !== 1 ? t('ingredients') : t('ingredient')}</span>
                    <span style={{ color: C.red }}>{filtered.filter(i => (parseFloat(i.current_stock) || 0) <= 0).length} {t('out_of_stock')}</span>
                    <span style={{ color: C.amber }}>{filtered.filter(i => { const s = parseFloat(i.current_stock) || 0; const m = parseFloat(i.minimum_stock_level) || 0; return s > 0 && m > 0 && s <= m; }).length} {t('low')}</span>
                </div>
            )}

            {/* Mobile hero + alert banner */}
            {isMobile && !loading && filtered.length > 0 && (() => {
                const totalValue = filtered.reduce((s, i) => s + (parseFloat(i.current_stock) || 0) * (parseFloat(i.cost_per_unit) || 0), 0);
                const outCount = filtered.filter(i => (parseFloat(i.current_stock) || 0) <= 0).length;
                const lowCount = filtered.filter(i => { const s = parseFloat(i.current_stock) || 0; const m = parseFloat(i.minimum_stock_level) || 0; return s > 0 && m > 0 && s <= m; }).length;
                const fmtMoney = (n) => `${currency}${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                    <>
                        <MobileHero
                            label={t('stock_value') || 'Stock Value'}
                            value={fmtMoney(totalValue)}
                            meta={`${filtered.length} ${filtered.length !== 1 ? (t('ingredients') || 'ingredients') : (t('ingredient') || 'ingredient')}`}
                            stats={[
                                { label: t('out_of_stock') || 'Out', value: String(outCount), tone: outCount > 0 ? 'red' : undefined },
                                { label: t('low') || 'Low', value: String(lowCount), tone: lowCount > 0 ? 'red' : undefined },
                                { label: t('ingredients') || 'Items', value: String(filtered.length) },
                            ]}
                        />
                        {(outCount > 0 || lowCount > 0) ? (
                            <MobileBanner
                                tone={outCount > 0 ? 'bad' : 'warn'}
                                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                                title={outCount > 0
                                    ? `${outCount} ${outCount !== 1 ? (t('ingredients') || 'ingredients') : (t('ingredient') || 'ingredient')} ${t('out_of_stock') || 'out of stock'}`
                                    : `${lowCount} ${t('low_stock') || 'low stock'}`}
                                subtitle={outCount > 0 && lowCount > 0
                                    ? `${lowCount} ${t('low_stock') || 'low stock'}`
                                    : (t('reorder_soon') || 'Reorder soon')}
                            />
                        ) : (
                            <MobileBanner
                                tone="good"
                                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                                title={t('stock_levels_healthy') || 'Stock levels healthy'}
                                subtitle={t('nothing_to_reorder') || 'Nothing to reorder'}
                            />
                        )}
                    </>
                );
            })()}

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 48, color: C.muted, fontSize: 13 }}>{t('loading')}</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<IconBox />}
                    title={search || statusFilter !== 'all' ? t('no_matches_found') : t('no_ingredients_yet')}
                    sub={search || statusFilter !== 'all' ? t('try_adjusting_search_or_filter') : t('add_first_ingredient_to_track_stock')}
                    cta={!search && statusFilter === 'all' ? t('add_ingredient') : undefined}
                    onCta={openAdd}
                />
            ) : isMobile ? (
                <div>
                    {filtered.map(item => {
                        const stock = parseFloat(item.current_stock) || 0;
                        const min = parseFloat(item.minimum_stock_level) || 0;
                        const cpu = parseFloat(item.cost_per_unit) || 0;
                        const isLow = min > 0 && stock <= min && stock > 0;
                        const isOut = stock <= 0;
                        const status = isOut
                            ? { label: t('out') || 'Out', tone: 'red' }
                            : isLow
                                ? { label: t('low') || 'Low', tone: 'amber' }
                                : { label: t('good') || 'Good', tone: 'green' };
                        const row = (label, value) => (
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                                <span style={{ color: '#475569' }}>{label}</span>
                                <span style={{ fontWeight: 700, color: C.text }}>{value}</span>
                            </div>
                        );
                        return (
                            <MobileCard
                                key={item.inventory_id}
                                accent={isOut ? 'red' : isLow ? 'amber' : undefined}
                                avatar={{ initials: (item.name || '?').slice(0, 2).toUpperCase(), tone: isOut ? 'red' : isLow ? 'amber' : 'gray' }}
                                title={item.name}
                                amount={`${stock} ${item.unit_of_measurement || ''}`}
                                status={status}
                                subtitle={item.supplier || item.unit_of_measurement}
                            >
                                {row(t('cost_per_unit') || 'Cost / Unit', `${currency}${cpu.toFixed(3)}/${item.unit_of_measurement || ''}`)}
                                {row(t('current_stock') || 'Current Stock', `${stock} ${item.unit_of_measurement || ''}`)}
                                {row(t('min_level') || 'Min Level', min > 0 ? `${min} ${item.unit_of_measurement || ''}` : '—')}
                                {row(t('supplier') || 'Supplier', item.supplier || '—')}
                                {row(t('total_cost') || 'Stock Value', `${currency}${(stock * cpu).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                    <button onClick={() => openTx(item)} style={{ flex: 1, padding: '11px 12px', borderRadius: 8, background: C.greenLight, color: C.green, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                        {t('transaction') || 'Transaction'}
                                    </button>
                                    <button onClick={() => openEdit(item)} style={{ flex: 1, padding: '11px 12px', borderRadius: 8, background: C.blueLight, color: C.blue, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                        {t('edit') || 'Edit'}
                                    </button>
                                    <button onClick={() => setHistoryItem(item)} style={{ padding: '11px 14px', borderRadius: 8, background: C.bg, color: C.muted, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t('cost_history') || 'Cost History'}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                    </button>
                                    <button onClick={() => openDelete(item)} style={{ padding: '11px 14px', borderRadius: 8, background: C.redLight, color: C.red, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} aria-label={t('delete') || 'Delete'}>
                                        <IconTrash />
                                    </button>
                                </div>
                            </MobileCard>
                        );
                    })}
                </div>
            ) : (
                <Table>
                    <thead>
                        <tr>
                            <TH>{t('name')}</TH>
                            <TH>{t('unit')}</TH>
                            <TH>{t('current_stock')}</TH>
                            <TH>{t('min_level')}</TH>
                            <TH>{t('cost_per_unit')}</TH>
                            <TH>{t('supplier')}</TH>
                            <TH>{t('status')}</TH>
                            <TH style={{ textAlign: 'right' }}>{t('actions')}</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(item => {
                            const stock = parseFloat(item.current_stock) || 0;
                            const min = parseFloat(item.minimum_stock_level) || 0;
                            const isLow = min > 0 && stock <= min && stock > 0;
                            const isOut = stock <= 0;
                            return (
                                <tr
                                    key={item.inventory_id}
                                    onClick={e => { if (e.target.closest('button')) return; openEdit(item); }}
                                    onMouseEnter={() => setHoveredRow(item.inventory_id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    style={{ background: hoveredRow === item.inventory_id ? C.bg : C.white, transition: 'background 0.12s', cursor: 'pointer' }}
                                >
                                    <TD><span style={{ fontWeight: 600 }}>{item.name}</span></TD>
                                    <TD><span style={{ color: C.muted }}>{item.unit_of_measurement}</span></TD>
                                    <TD>
                                        <span style={{ fontWeight: 600, color: isOut ? C.red : isLow ? C.amber : C.text }}>
                                            {stock}
                                        </span>
                                        <span style={{ color: C.muted, fontSize: 12, marginLeft: 4 }}>{item.unit_of_measurement}</span>
                                    </TD>
                                    <TD>
                                        {min > 0 ? <span style={{ color: C.muted }}>{min} {item.unit_of_measurement}</span> : <span style={{ color: C.border }}>—</span>}
                                    </TD>
                                    <TD>
                                        <span style={{ fontWeight: 600 }}>{currency}{parseFloat(item.cost_per_unit || 0).toFixed(3)}</span>
                                        <span style={{ color: C.muted, fontSize: 12 }}>/{item.unit_of_measurement}</span>
                                    </TD>
                                    <TD><span style={{ color: C.muted }}>{item.supplier || '—'}</span></TD>
                                    <TD><StockBadge item={item} t={t} /></TD>
                                    <TD style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                            <button onClick={() => setHistoryItem(item)} title={t('cost_history')} style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 14 }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                            </button>
                                            <button onClick={() => openTx(item)} title={t('record_transaction')} style={{ background: C.greenLight, color: C.green, border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                                                {t('transaction')}
                                            </button>
                                            <button onClick={() => openEdit(item)} title={t('edit')} style={{ background: C.blueLight, color: C.blue, border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <IconEdit />
                                            </button>
                                            <button onClick={() => openDelete(item)} title={t('delete')} style={{ background: C.redLight, color: C.red, border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <IconTrash />
                                            </button>
                                        </div>
                                    </TD>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}


            {/* Add / Edit Modal */}
            <Modal open={addEditOpen} onClose={() => setAddEditOpen(false)} title={editingItem ? t('edit_ingredient') : t('add_ingredient')}>
                <form onSubmit={handleSaveItem} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Label required>{t('name')}</Label>
                        <Input name="name" value={form.name} onChange={handleFormChange} placeholder={t('placeholder_ingredient_name')} required />
                    </div>
                    {/* Translation section */}
                    <div>
                        {form.translation_mode && !showTranslationPicker && (translationOptions || (form.translation_mode === 'manual' && form.manual_translation?.ar)) ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" onClick={async () => { setShowTranslationPicker(true); if (!translationOptions) await handleTranslate(); }} disabled={!form.name}
                                    style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 600, cursor: form.name ? 'pointer' : 'not-allowed', opacity: form.name ? 1 : 0.5 }}>
                                    {t('modify_translation')}
                                </button>
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                    {form.translation_mode === 'manual'
                                        ? `${t('manual_entry')}: ${form.manual_translation?.ar || '\u2014'}`
                                        : form.translation_mode === 'translation'
                                            ? `${t('translation')}: ${translationOptions?.translation || '\u2014'}`
                                            : `${t('transliteration')}: ${translationOptions?.transliteration || '\u2014'}`
                                    }
                                </span>
                            </div>
                        ) : !showTranslationPicker ? (
                            <button type="button" onClick={handleTranslate} disabled={translating || !form.name}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid #bfdbfe', background: '#eff5ff', color: '#1d4ed8', fontSize: 11, fontWeight: 600, cursor: (translating || !form.name) ? 'not-allowed' : 'pointer', opacity: (translating || !form.name) ? 0.5 : 1 }}>
                                {translating ? `${t('translating')}...` : t('translate')}
                            </button>
                        ) : null}

                        {showTranslationPicker && translationOptions?.suggestion && translationOptions.suggestion !== form.name && (
                            <div style={{ marginTop: 6, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{t('did_you_mean')} <strong>"{translationOptions.suggestion}"</strong>?</span>
                                <button type="button" onClick={() => setForm(f => ({ ...f, name: translationOptions.suggestion }))} style={{ background: 'none', border: 'none', textDecoration: 'underline', color: '#92400e', cursor: 'pointer', fontSize: 11 }}>{t('use_this')}</button>
                            </div>
                        )}

                        {showTranslationPicker && translationOptions && (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {translationOptions.translation && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'translation' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'translation' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="ingredient_translation_mode" checked={form.translation_mode === 'translation'} onChange={() => setForm(f => ({ ...f, translation_mode: 'translation', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('translation')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.translation}</span>
                                    </label>
                                )}
                                {translationOptions.transliteration && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'transliteration' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'transliteration' ? '#eff5ff' : '#fff' }}>
                                        <input type="radio" name="ingredient_translation_mode" checked={form.translation_mode === 'transliteration'} onChange={() => setForm(f => ({ ...f, translation_mode: 'transliteration', manual_translation: null }))} style={{ accentColor: '#2563eb' }} />
                                        <span style={{ fontSize: 12, color: '#475569' }}>{t('transliteration')}:</span>
                                        <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{translationOptions.transliteration}</span>
                                    </label>
                                )}
                                {translationOptions.community_manuals?.length > 0 && (
                                    <div style={{ paddingLeft: 8 }}>
                                        <p style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{t('community_suggestions')}</p>
                                        {translationOptions.community_manuals.map((m, i) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: '1.5px solid', borderColor: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: (form.translation_mode === 'manual' && form.manual_translation?.ar === m) ? '#eff5ff' : '#fff', marginBottom: 4 }}>
                                                <input type="radio" name="ingredient_translation_mode" checked={form.translation_mode === 'manual' && form.manual_translation?.ar === m} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: { ar: m } }))} style={{ accentColor: '#2563eb' }} />
                                                <span style={{ fontSize: 12, fontWeight: 600 }} dir="rtl">{m}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1.5px solid', borderColor: form.translation_mode === 'manual' ? '#2563eb' : '#e2e8f0', borderRadius: 7, cursor: 'pointer', background: form.translation_mode === 'manual' ? '#eff5ff' : '#fff' }}>
                                    <input type="radio" name="ingredient_translation_mode" checked={form.translation_mode === 'manual'} onChange={() => setForm(f => ({ ...f, translation_mode: 'manual', manual_translation: f.manual_translation || { ar: '' } }))} style={{ accentColor: '#2563eb' }} />
                                    <span style={{ fontSize: 12, color: '#475569' }}>{t('manual_entry')}</span>
                                </label>
                                {form.translation_mode === 'manual' && (
                                    <input type="text" dir="rtl" placeholder={t('enter_arabic_name')} value={form.manual_translation?.ar || ''} onChange={e => setForm(f => ({ ...f, manual_translation: { ar: e.target.value } }))}
                                        style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box', color: '#212529', textAlign: 'right' }} />
                                )}
                                {form.translation_mode && (
                                    <button type="button" onClick={async () => {
                                        setSaving(true);
                                        try {
                                            const payload = { ...form, cost_per_unit: parseFloat(form.cost_per_unit), current_stock: parseFloat(form.current_stock) || 0, minimum_stock_level: form.minimum_stock_level !== '' ? parseFloat(form.minimum_stock_level) : null, recipe_unit: form.recipe_unit || null, purchase_unit: form.purchase_unit?.trim() || null, purchase_unit_size: form.purchase_unit?.trim() && form.purchase_unit_size !== '' ? parseFloat(form.purchase_unit_size) : null, translation_mode: form.translation_mode || null, manual_translation: form.manual_translation || null };
                                            if (editingItem) {
                                                await inventoryService.updateInventoryItem(editingItem.inventory_id, payload);
                                            }
                                            setShowTranslationPicker(false);
                                        } catch { toast(t('failed_to_save_ingredient'), 'error'); }
                                        finally { setSaving(false); }
                                    }} disabled={saving || (form.translation_mode === 'manual' && !form.manual_translation?.ar)}
                                        style={{ marginTop: 4, padding: '7px 16px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', opacity: (saving || (form.translation_mode === 'manual' && !form.manual_translation?.ar)) ? 0.5 : 1 }}>
                                        {saving ? `${t('saving')}...` : t('save_translation')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Instructions */}
                    <p style={{ fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.5 }}>{t('ingredient_instructions')}</p>
                    <div>
                        <Label required>{t('unit_of_measurement')}</Label>
                        <Select name="unit_of_measurement" value={form.unit_of_measurement} onChange={(e) => { handleFormChange(e); setForm(f => ({ ...f, recipe_unit: null })); }} required>
                            <option value="">{t('select_unit')}</option>
                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                        </Select>
                    </div>
                    {/* Recipe Unit — only when stock unit has a conversion (litres→ml, kg→grams) */}
                    {form.unit_of_measurement && hasConversion(form.unit_of_measurement) && (
                        <div>
                            <Label>{t('recipe_unit')}</Label>
                            <Select name="recipe_unit" value={form.recipe_unit || form.unit_of_measurement} onChange={handleFormChange}>
                                {getRecipeUnitOptions(form.unit_of_measurement).map(u => <option key={u} value={u}>{u}</option>)}
                            </Select>
                            <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0' }}>{t('recipe_unit_helper')}</p>
                        </div>
                    )}
                    {/* Purchase Unit fields */}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <Label>{t('purchase_unit')}</Label>
                            <Input name="purchase_unit" value={form.purchase_unit} onChange={handleFormChange} placeholder={t('placeholder_purchase_unit')} />
                        </div>
                        <div>
                            <Label>{t('purchase_unit_size')}{form.purchase_unit && form.unit_of_measurement ? ` (${form.unit_of_measurement} ${t('per_unit')} ${form.purchase_unit})` : ''}</Label>
                            <Input type="number" name="purchase_unit_size" value={form.purchase_unit_size} onChange={handleFormChange} min="0" step="0.01" placeholder={t('placeholder_unit_size')} disabled={!form.purchase_unit} />
                        </div>
                    </div>
                    {form.purchase_unit && form.purchase_unit_size && form.unit_of_measurement && (
                        <p style={{ fontSize: 11, color: C.muted, margin: '-8px 0 0' }}>{t('purchase_unit_helper')} — 1 {form.purchase_unit} = {form.purchase_unit_size} {form.unit_of_measurement}</p>
                    )}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            {form.purchase_unit && parseFloat(form.purchase_unit_size) > 0 ? (
                                <>
                                    <Label>{t('quantity')} ({form.purchase_unit})</Label>
                                    <Input
                                        type="number"
                                        value={form._purchase_qty ?? ''}
                                        onChange={e => {
                                            const pqty = e.target.value;
                                            const stockQty = pqty ? parseFloat(pqty) * parseFloat(form.purchase_unit_size) : 0;
                                            const qty = parseFloat(pqty) || 0;
                                            const total = parseFloat(form._total_cost) || 0;
                                            const cpu = qty > 0 && total > 0 ? (total / stockQty).toFixed(4) : form.cost_per_unit;
                                            setForm(f => ({ ...f, _purchase_qty: pqty, current_stock: stockQty, cost_per_unit: cpu }));
                                        }}
                                        min="0" step="1"
                                    />
                                    {parseFloat(form._purchase_qty) > 0 && (
                                        <p style={{ margin: '4px 0 0', fontSize: 11, color: C.green, fontWeight: 600 }}>
                                            = {parseFloat(form.current_stock).toFixed(1)} {form.unit_of_measurement} {t('in_stock')}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Label>{t('quantity')} {form.unit_of_measurement ? `(${form.unit_of_measurement})` : ''}</Label>
                                    <Input type="number" name="current_stock" value={form.current_stock} onChange={e => {
                                        const qty = parseFloat(e.target.value) || 0;
                                        const total = parseFloat(form._total_cost) || 0;
                                        setForm(f => ({ ...f, current_stock: e.target.value, cost_per_unit: qty > 0 && total > 0 ? (total / qty).toFixed(4) : f.cost_per_unit }));
                                    }} min="0" step="0.01" />
                                </>
                            )}
                        </div>
                        <div>
                            <Label>{t('total_cost')} ({currency})</Label>
                            <Input type="number" value={form._total_cost} onChange={e => {
                                const total = parseFloat(e.target.value) || 0;
                                const qty = parseFloat(form.current_stock) || 0;
                                setForm(f => ({ ...f, _total_cost: e.target.value, cost_per_unit: qty > 0 && total > 0 ? (total / qty).toFixed(4) : f.cost_per_unit }));
                            }} min="0" step="0.01" placeholder="0.00" />
                        </div>
                    </div>
                    {(parseFloat(form.cost_per_unit) > 0) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 0 0', padding: '8px 12px', background: C.greenLight, borderRadius: 8 }}>
                            <span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>{t('cost_per_unit')}: {currency}{parseFloat(form.cost_per_unit).toFixed(4)}/{form.unit_of_measurement || t('unit')}</span>
                            {editingItem && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const yesterday = new Date();
                                        yesterday.setDate(yesterday.getDate() - 1);
                                        setCorrectionForm({
                                            cost_per_unit: editingItem.cost_per_unit || '',
                                            effective_from: yesterday.toISOString().split('T')[0],
                                            reason: '',
                                        });
                                        setCorrectionOpen(true);
                                    }}
                                    style={{
                                        marginLeft: 'auto', background: 'none', border: 'none',
                                        padding: 0, color: C.blue, fontSize: 11, fontWeight: 600,
                                        cursor: 'pointer', textDecoration: 'underline',
                                    }}
                                >
                                    {t('correct_past_cost')}
                                </button>
                            )}
                        </div>
                    )}
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <Label>{t('minimum_stock_level')}</Label>
                            <Input type="number" name="minimum_stock_level" value={form.minimum_stock_level} onChange={handleFormChange} min="0" step="0.01" placeholder={t('optional')} />
                        </div>
                        <div>
                            <Label>{t('supplier')}</Label>
                            <Input name="supplier" value={form.supplier} onChange={handleFormChange} placeholder={t('optional')} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8 }}>
                        <BtnSecondary type="button" onClick={() => setAddEditOpen(false)}>{t('cancel')}</BtnSecondary>
                        <BtnPrimary type="submit" disabled={saving}>{saving ? t('saving') : editingItem ? t('update') : t('add_ingredient')}</BtnPrimary>
                    </div>
                </form>
            </Modal>

            {/* Transaction Modal */}
            <Modal open={txOpen} onClose={() => setTxOpen(false)} title={`${t('record_transaction')} — ${txItem?.name || ''}`}>
                <form onSubmit={handleTxSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {txItem && (
                        <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${C.border}` }}>
                            <StockBadge item={txItem} t={t} />
                            <span style={{ fontSize: 13, color: C.muted }}>
                                {t('current_stock')}: <strong style={{ color: C.text }}>{txItem.current_stock} {txItem.unit_of_measurement}</strong>
                            </span>
                        </div>
                    )}
                    <div>
                        <Label required>{t('transaction_type')}</Label>
                        <Select name="transaction_type" value={tx.transaction_type} onChange={handleTxChange} required>
                            <option value="purchase">{t('purchase_stock_in')}</option>
                            <option value="adjustment">{t('stock_adjustment')}</option>
                            <option value="waste">{t('waste_loss')}</option>
                        </Select>
                    </div>
                    {/* Purchase unit toggle — only for purchases when ingredient has a purchase_unit */}
                    {tx.transaction_type === 'purchase' && txItem?.purchase_unit && txItem?.purchase_unit_size && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button type="button" onClick={() => setTx(prev => ({ ...prev, use_purchase_unit: true, purchase_quantity: '', quantity: '' }))}
                                style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1.5px solid ${tx.use_purchase_unit ? C.green : C.border}`, background: tx.use_purchase_unit ? C.greenLight : C.white, color: tx.use_purchase_unit ? C.green : C.muted, cursor: 'pointer' }}>
                                {t('enter_in')} {txItem.purchase_unit}
                            </button>
                            <button type="button" onClick={() => setTx(prev => ({ ...prev, use_purchase_unit: false, purchase_quantity: '', quantity: '' }))}
                                style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: `1.5px solid ${!tx.use_purchase_unit ? C.green : C.border}`, background: !tx.use_purchase_unit ? C.greenLight : C.white, color: !tx.use_purchase_unit ? C.green : C.muted, cursor: 'pointer' }}>
                                {t('enter_in')} {txItem.unit_of_measurement}
                            </button>
                        </div>
                    )}
                    {/* Purchase quantity input */}
                    {tx.use_purchase_unit && txItem?.purchase_unit && txItem?.purchase_unit_size && tx.transaction_type === 'purchase' ? (
                        <div>
                            <Label required>{t('quantity')} ({txItem.purchase_unit})</Label>
                            <Input type="number" name="purchase_quantity" value={tx.purchase_quantity} onChange={handleTxChange} min="0" step="0.01" required placeholder="0.00" />
                            {tx.purchase_quantity && parseFloat(tx.purchase_quantity) > 0 && (
                                <p style={{ fontSize: 12, color: C.green, margin: '6px 0 0', fontWeight: 600 }}>
                                    = {(parseFloat(tx.purchase_quantity) * txItem.purchase_unit_size).toFixed(2)} {txItem.unit_of_measurement} {t('will_be_added')}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div>
                            <Label required>{t('quantity')} ({txItem?.unit_of_measurement})</Label>
                            <Input type="number" name="quantity" value={tx.quantity} onChange={handleTxChange} min="0" step="0.01" required placeholder="0.00" />
                            {tx.transaction_type === 'adjustment' && (
                                <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0' }}>{t('sets_stock_to_exact_value')}</p>
                            )}
                            {tx.transaction_type === 'waste' && (
                                <p style={{ fontSize: 11, color: C.red, margin: '4px 0 0' }}>{t('amount_subtracted_from_stock')}</p>
                            )}
                        </div>
                    )}
                    {tx.transaction_type === 'purchase' && (
                        <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <Label>{t('cost_per_unit')} ({currency})</Label>
                                <Input type="number" name="cost_per_unit" value={tx.cost_per_unit} onChange={handleTxChange} min="0" step="0.001" placeholder="0.000" />
                            </div>
                            <div>
                                <Label>{t('total_cost')} ({currency})</Label>
                                <Input type="number" name="total_cost" value={tx.total_cost} onChange={handleTxChange} min="0" step="0.01" placeholder={t('auto_calculated')} />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label>{t('notes')}</Label>
                        <Textarea name="notes" value={tx.notes} onChange={handleTxChange} placeholder={t('optional_note')} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8 }}>
                        <BtnSecondary type="button" onClick={() => setTxOpen(false)}>{t('cancel')}</BtnSecondary>
                        <BtnPrimary type="submit" disabled={saving}>{saving ? t('saving') : t('record_transaction')}</BtnPrimary>
                    </div>
                </form>
            </Modal>

            {/* Delete confirm */}
            <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('delete_ingredient')} width={400}>
                <p style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>
                    {t('confirm_delete')} <strong style={{ color: C.text }}>{deleteTarget?.name}</strong>? {t('cannot_be_undone')}
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <BtnSecondary onClick={() => setDeleteOpen(false)}>{t('cancel')}</BtnSecondary>
                    <BtnDanger onClick={handleDeleteItem} disabled={saving}>{saving ? t('deleting') : t('delete')}</BtnDanger>
                </div>
            </Modal>

            {/* Cost correction — backdated */}
            <Modal
                open={correctionOpen}
                onClose={() => setCorrectionOpen(false)}
                title={`${t('correct_past_cost')}${editingItem?.name ? ' — ' + editingItem.name : ''}`}
                width={460}
            >
                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        if (!editingItem) return;
                        if (!correctionForm.cost_per_unit || !correctionForm.effective_from || !correctionForm.reason) {
                            toast(t('all_fields_required'), 'error');
                            return;
                        }
                        try {
                            setSaving(true);
                            await inventoryService.correctInventoryCost(editingItem.inventory_id, {
                                cost_per_unit: parseFloat(correctionForm.cost_per_unit),
                                effective_from: correctionForm.effective_from,
                                reason: correctionForm.reason,
                            });
                            toast(t('correction_recorded'), 'success');
                            setCorrectionOpen(false);
                            setAddEditOpen(false);
                            await loadItems();
                        } catch (err) {
                            toast(err?.response?.data?.detail || t('correction_failed'), 'error');
                        } finally {
                            setSaving(false);
                        }
                    }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                    <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>
                        {t('cost_correction_explainer')}
                    </p>
                    <div>
                        <Label required>{t('corrected_cost_per_unit')} ({currency})</Label>
                        <Input
                            type="number" min="0.001" step="0.001"
                            value={correctionForm.cost_per_unit}
                            onChange={e => setCorrectionForm(p => ({ ...p, cost_per_unit: e.target.value }))}
                            required
                        />
                    </div>
                    <div>
                        <Label required>{t('effective_from_past_date')}</Label>
                        <Input
                            type="date"
                            max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                            value={correctionForm.effective_from}
                            onChange={e => setCorrectionForm(p => ({ ...p, effective_from: e.target.value }))}
                            required
                        />
                    </div>
                    <div>
                        <Label required>{t('correction_reason')}</Label>
                        <Input
                            type="text" maxLength={500}
                            value={correctionForm.reason}
                            onChange={e => setCorrectionForm(p => ({ ...p, reason: e.target.value }))}
                            placeholder={t('correction_reason_placeholder')}
                            required
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                        <BtnSecondary type="button" onClick={() => setCorrectionOpen(false)}>{t('cancel')}</BtnSecondary>
                        <BtnPrimary type="submit" disabled={saving}>{saving ? t('saving') : t('save_correction')}</BtnPrimary>
                    </div>
                </form>
            </Modal>

            <CostHistoryDrawer
                item={historyItem}
                open={!!historyItem}
                onClose={() => setHistoryItem(null)}
                t={t}
                currency={currency}
            />

            <RecipeImportModal
                open={showRecipeImport}
                onClose={() => setShowRecipeImport(false)}
                onImported={() => loadItems()}
                t={t}
            />

            <BulkAddModal
                open={showBulkAdd}
                onClose={() => setShowBulkAdd(false)}
                onImport={(results) => {
                    loadItems();
                    toast(t('bulk_import_success').replace('{count}', results.success), 'success');
                }}
                config={{
                    title: 'bulk_add_ingredients',
                    instructions: 'bulk_add_ingredients_instructions',
                    columns: [
                        { key: 'name', label: 'Name', required: true, type: 'text' },
                        { key: 'unit', label: 'Unit', required: true, type: 'dropdown', options: UNIT_OPTIONS },
                        { key: 'purchase_unit', label: 'Purchase Unit', type: 'text' },
                        { key: 'unit_size', label: 'Unit Size', type: 'number' },
                        { key: 'quantity', label: 'Quantity', type: 'number' },
                        { key: 'total_cost', label: 'Total Cost', type: 'number' },
                        { key: 'cost_per_unit', label: 'Cost Per Unit', type: 'number', readOnly: true },
                        { key: 'minimum_stock', label: 'Minimum Stock', type: 'number' },
                        { key: 'supplier', label: 'Supplier', type: 'text' },
                    ],
                    emptyRow: () => ({ name: '', unit: '', purchase_unit: '', unit_size: '', quantity: '', total_cost: '', cost_per_unit: '', minimum_stock: '', supplier: '' }),
                    transforms: (row) => {
                        const qty = parseFloat(row.quantity) || 0;
                        const total = parseFloat(row.total_cost) || 0;
                        const unitSize = parseFloat(row.unit_size) || 0;
                        const stockQty = unitSize > 0 ? qty * unitSize : qty;
                        return { ...row, cost_per_unit: stockQty > 0 && total > 0 ? (total / stockQty).toFixed(4) : '' };
                    },
                    existingNames: items.map(i => i.name),
                    templateFilename: 'ingredients_template.csv',
                    exampleRows: [
                        { name: 'Coffee Beans', unit: 'kg', purchase_unit: 'bags', unit_size: '5', quantity: '5', total_cost: '312.50', cost_per_unit: '12.5000', minimum_stock: '5', supplier: 'Bean Co' },
                        { name: 'Whole Milk', unit: 'liters', purchase_unit: 'bottles', unit_size: '2', quantity: '20', total_cost: '48.00', cost_per_unit: '1.2000', minimum_stock: '10', supplier: 'Dairy Fresh' },
                    ],
                    createItem: async (row) => {
                        const unitSize = parseFloat(row.unit_size) || 0;
                        const qty = parseFloat(row.quantity) || 0;
                        const stockQty = unitSize > 0 ? qty * unitSize : qty;
                        const conv = UNIT_CONVERSIONS[row.unit];
                        return inventoryService.createInventoryItem({
                            name: row.name,
                            unit_of_measurement: row.unit,
                            cost_per_unit: parseFloat(row.cost_per_unit) || 0,
                            current_stock: stockQty,
                            minimum_stock_level: parseFloat(row.minimum_stock) || 0,
                            supplier: row.supplier || null,
                            purchase_unit: row.purchase_unit?.trim() || null,
                            purchase_unit_size: unitSize > 0 ? unitSize : null,
                            recipe_unit: conv ? conv.small : null,
                        });
                    },
                }}
                t={t}
            />
        </div>
    );
};


// ─── Main page ────────────────────────────────────────────────────────────────

const PortalInventoryPage = () => {
    const { toasts, add: toast } = useToast();
    const { t } = useLanguage();

    // Recipes are now managed inside the Product modal (Products page), so this
    // page is single-purpose: manage raw ingredients / inventory items.
    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, color: C.text, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <ToastContainer toasts={toasts} />

                {/* Page header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>{t('inventory')}</h1>
                        <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>{t('manage_stock_levels_and_costs')}</p>
                    </div>
                </div>

                <IngredientsTab toast={toast} />
            </div>
        </PortalLayout>
    );
};

export default PortalInventoryPage;
